/**
 * Custom icons that don't exist in Iconoir.
 * These are kept separate and will be replaced when @wayflyer/icons package is ready.
 * See MISSING_ICONS.md for tracking.
 */

import type React from "react";

import { cn } from "@/lib/utils";

import type { IconSize } from "./types";
import { SIZE_CLASSES, STROKE_WEIGHTS } from "./types";

type CustomIconProps = React.PropsWithoutRef<React.SVGProps<SVGSVGElement>>;

// Helper to create custom icons with consistent sizing
interface CustomIconWrapperProps extends Omit<
  CustomIconProps,
  "width" | "height"
> {
  className?: string;
}

function createCustomIcon(
  SvgComponent: React.FC<CustomIconProps & { strokeWidth: number }>,
  size: IconSize
): React.FC<CustomIconWrapperProps> {
  const strokeWeight = STROKE_WEIGHTS[size];
  const sizeClass = SIZE_CLASSES[size];

  const WrappedIcon: React.FC<CustomIconWrapperProps> = ({
    className,
    ...props
  }) => (
    <SvgComponent
      {...props}
      width={size as number}
      height={size as number}
      strokeWidth={strokeWeight}
      className={cn(sizeClass, className)}
    />
  );

  return WrappedIcon;
}

// Base SVG components for custom icons

const ArrowTopRightOnSquareSvg: React.FC<
  CustomIconProps & { strokeWidth: number }
> = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    <path d="M15.75 14H17v5.41A2.59 2.59 0 0 1 14.41 22H4.59A2.59 2.59 0 0 1 2 19.41V9.6a2.59 2.59 0 0 1 2.59-2.59H10v1.25H4.59c-.74 0-1.34.6-1.34 1.34v9.81c0 .74.6 1.34 1.34 1.34h9.82c.74 0 1.34-.6 1.34-1.34zM12 2v1.25h7.86L8.55 14.56l.88.88L20.74 4.13v7.86h1.25V2h-10Z" />
  </svg>
);

// Export arrow-top-right-on-square (share/external link icon) - closest to ShareIos but different
export const IconArrowTopRightOnSquare12Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  12
);
export const IconArrowTopRightOnSquare16Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  16
);
export const IconArrowTopRightOnSquare20Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  20
);
export const IconArrowTopRightOnSquare24Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  24
);
export const IconArrowTopRightOnSquare32Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  32
);
export const IconArrowTopRightOnSquare48Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  48
);
export const IconArrowTopRightOnSquare64Line = createCustomIcon(
  ArrowTopRightOnSquareSvg,
  64
);

// ArrowRightCircle with filled background (used as CTA button icon)
// This is different from Iconoir's ArrowRightCircle - it's a filled circle with arrow
type ArrowRightCircleFilledProps = CustomIconWrapperProps & {
  onColor?: boolean;
};

const ArrowRightCircleFilledSvg: React.FC<
  ArrowRightCircleFilledProps & { size: number }
> = ({ className, onColor, size, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    className={cn("stroke-0", className)}
    {...props}
  >
    <rect width="24" height="24" rx="12" fill="currentColor" />
    <path
      d="M17 12L13.395 15.58L12.955 15.135L15.8 12.31H7V11.685H15.795L12.95 8.85998L13.39 8.41498L16.995 11.995L17 12Z"
      fill={onColor ? "#1C1719" : "white"}
    />
  </svg>
);

function createArrowRightCircleFilledIcon(
  size: IconSize
): React.FC<ArrowRightCircleFilledProps> {
  const sizeClass = SIZE_CLASSES[size];

  const WrappedIcon: React.FC<ArrowRightCircleFilledProps> = ({
    className,
    onColor,
    ...props
  }) => (
    <ArrowRightCircleFilledSvg
      {...props}
      size={size}
      onColor={onColor}
      className={cn(sizeClass, className)}
    />
  );

  return WrappedIcon;
}

export const IconArrowRightCircleFilled12Line =
  createArrowRightCircleFilledIcon(12);
export const IconArrowRightCircleFilled16Line =
  createArrowRightCircleFilledIcon(16);
export const IconArrowRightCircleFilled20Line =
  createArrowRightCircleFilledIcon(20);
export const IconArrowRightCircleFilled24Line =
  createArrowRightCircleFilledIcon(24);
export const IconArrowRightCircleFilled32Line =
  createArrowRightCircleFilledIcon(32);
export const IconArrowRightCircleFilled48Line =
  createArrowRightCircleFilledIcon(48);
export const IconArrowRightCircleFilled64Line =
  createArrowRightCircleFilledIcon(64);

// Placeholder exports for missing icons - to be implemented
// These are documented in MISSING_ICONS.md

/**
 * TODO: Implement these icons when SVGs are available from design team:
 * - IconChartPieAlt2 (DT-154)
 * - IconArrowsRightLeft (DT-157)
 * - IconConnections (DT-155)
 * - IconTodo
 * - IconScale
 * - IconBuildingGov
 * - IconPaymentCardReplace
 * - IconMoneyCheck
 * - IconCircleSlash
 * - IconCoinArrowRight
 * - IconPauseCircle
 * - IconInbox
 * - IconReceiptRefund
 * - IconClockFast
 * - IconDocumentTable
 * - IconStairs
 * - IconBanknoteSlot
 * - IconChatBubbleSmile
 * - IconCheckSquares
 * - IconSlidersVertical
 * - IconSignature
 * - IconBanknotesAlt4
 * - IconStopCircle
 * - IconCalendarDots
 */
